import React, { useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bool, func } from 'prop-types';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { fetchCurrentUserHasListings } from '../../ducks/user.duck';
import { recheckProviderRequirements } from '../../util/recheckProviderRequirements';
import { isStripePayoutsEnabled } from '../../util/providerRequirementsCheck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';
import { IconCheckmark, IconClose, IconArrowHead } from '../../components';

import css from './ProviderRequirementsChecklist.module.css';

const RequirementLink = ({ name, hash, className, onClick, children }) => {
  const routeConfiguration = useRouteConfiguration();
  const pathname = pathByRouteName(name, routeConfiguration);
  // Hash deep-links scroll to the matching section id via Routes.js
  // setPageScrollPosition — also when already on the target page.
  const to = hash ? { pathname, hash } : pathname;
  return (
    <Link to={to} className={className} onClick={onClick}>
      {children}
    </Link>
  );
};

/**
 * Live provider requirements checklist (progress + six rows).
 * Self-fetching via Redux; safe to mount inside HelpFlyout or a page.
 */
export const ProviderRequirementsChecklistComponent = ({
  currentUser = null,
  hasListings = false,
  stripeAccount = null,
  stripeAccountFetched = false,
  fetchStripeAccountInProgress = false,
  fetchStripeAccountError = null,
  onFetchCurrentUserHasListings,
  onFetchStripeAccount,
  onRecheckProviderRequirements,
  onRequirementNavigate,
}) => {
  const intl = useIntl();
  const didFetchListingsRef = useRef(false);
  const didFetchStripeRef = useRef(false);
  const didRecheckRef = useRef(false);

  useEffect(() => {
    if (!currentUser || didFetchListingsRef.current || !onFetchCurrentUserHasListings) {
      return;
    }
    didFetchListingsRef.current = true;
    onFetchCurrentUserHasListings();
  }, [currentUser, onFetchCurrentUserHasListings]);

  useEffect(() => {
    if (!currentUser || !onFetchStripeAccount) {
      return;
    }
    // No Stripe account exists yet — fetching would be a guaranteed 404.
    if (!currentUser?.attributes?.stripeConnected) {
      return;
    }
    // Fetch once unless we already have a fetched account with payout status.
    if (didFetchStripeRef.current || (stripeAccountFetched && isStripePayoutsEnabled(stripeAccount))) {
      return;
    }
    if (fetchStripeAccountInProgress) {
      return;
    }
    didFetchStripeRef.current = true;
    onFetchStripeAccount();
  }, [
    currentUser,
    fetchStripeAccountInProgress,
    onFetchStripeAccount,
    stripeAccount,
    stripeAccountFetched,
  ]);

  useEffect(() => {
    if (!currentUser || didRecheckRef.current || !onRecheckProviderRequirements) {
      return;
    }
    didRecheckRef.current = true;
    onRecheckProviderRequirements();
  }, [currentUser, onRecheckProviderRequirements]);

  const user = ensureCurrentUser(currentUser);
  const { publicData, protectedData } = user?.attributes?.profile || {};
  const profileImage = user?.profileImage;

  const stripeConnected = !!user?.attributes?.stripeConnected;
  const stripeComplete = isStripePayoutsEnabled(stripeAccount);
  // Avoid flashing incomplete while the first Stripe account fetch is in flight.
  // Only pending when an account exists to fetch and the fetch hasn't finished
  // (successfully or with an error) yet.
  const stripePending =
    !stripeComplete &&
    stripeConnected &&
    (fetchStripeAccountInProgress || (!stripeAccountFetched && !fetchStripeAccountError));

  const requirements = [
    {
      key: 'profilePicture',
      label: intl.formatMessage({ id: 'ProviderRequirementsPage.profilePicture' }),
      completed: !!(profileImage && profileImage.id),
      pending: false,
      description: intl.formatMessage({ id: 'ProviderRequirementsPage.profilePictureDescription' }),
      linkPage: 'ProfileSettingsPage',
      linkHash: '#profile-picture',
    },
    {
      key: 'workingWithChildrenCheck',
      label: intl.formatMessage({ id: 'ProviderRequirementsPage.workingWithChildrenCheck' }),
      completed: !!protectedData?.wwwcFileUpload,
      pending: false,
      description: intl.formatMessage({
        id: 'ProviderRequirementsPage.workingWithChildrenCheckDescription',
      }),
      linkPage: 'ProfileSettingsPage',
      linkHash: '#field-prot_wwwcFileUpload',
    },
    {
      key: 'criminalHistoryCheck',
      label: intl.formatMessage({ id: 'ProviderRequirementsPage.criminalHistoryCheck' }),
      completed: !!protectedData?.chcFileUpload,
      pending: false,
      description: intl.formatMessage({
        id: 'ProviderRequirementsPage.criminalHistoryCheckDescription',
      }),
      linkPage: 'ProfileSettingsPage',
      linkHash: '#field-prot_chcFileUpload',
    },
    {
      key: 'abnNumber',
      label: intl.formatMessage({ id: 'ProviderRequirementsPage.abnNumber' }),
      completed: !!publicData?.prov_abn,
      pending: false,
      description: intl.formatMessage({ id: 'ProviderRequirementsPage.abnNumberDescription' }),
      linkPage: 'ProfileSettingsPage',
      linkHash: '#field-pub_prov_abn',
    },
    {
      key: 'stripePayoutDetails',
      label: intl.formatMessage({ id: 'ProviderRequirementsPage.stripePayoutDetails' }),
      completed: stripeComplete,
      pending: stripePending,
      description: intl.formatMessage({
        id: 'ProviderRequirementsPage.stripePayoutDetailsDescription',
      }),
      linkPage: 'StripePayoutPage',
    },
    {
      key: 'firstListing',
      label: intl.formatMessage({ id: 'ProviderRequirementsPage.firstListing' }),
      completed: hasListings,
      pending: false,
      description: intl.formatMessage({ id: 'ProviderRequirementsPage.firstListingDescription' }),
      linkPage: 'ProviderServicesPage',
    },
  ];

  const completedCount = requirements.filter(req => req.completed).length;
  const totalCount = requirements.length;

  const renderRequirementBody = requirement => (
    <div className={css.requirementHeader}>
      <div className={css.requirementIcon}>
        {requirement.completed ? (
          <IconCheckmark className={css.completedIcon} />
        ) : requirement.pending ? (
          <span className={css.pendingIcon} aria-hidden="true" />
        ) : (
          <IconClose className={css.incompleteIcon} />
        )}
      </div>
      <div className={css.requirementContent}>
        <h3 className={css.requirementTitle}>{requirement.label}</h3>
        <p className={css.requirementDescription}>{requirement.description}</p>
      </div>
      <div className={css.requirementStatus}>
        {requirement.completed ? (
          <span className={css.completedStatus}>
            <FormattedMessage id="ProviderRequirementsPage.completed" />
          </span>
        ) : requirement.pending ? (
          <span className={css.pendingStatus}>
            <FormattedMessage id="ProviderRequirementsPage.checking" />
          </span>
        ) : (
          <span className={css.incompleteStatus}>
            <FormattedMessage id="ProviderRequirementsPage.incomplete" />
            <IconArrowHead className={css.arrowIcon} />
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className={css.root} data-testid="ProviderRequirementsChecklist">
      <p className={css.introText}>
        <FormattedMessage
          id="ProviderRequirementsPage.intro"
          values={{
            emailLink: (
              <a href="mailto:help@findyourkind.com">help@findyourkind.com</a>
            ),
          }}
        />
      </p>
      <div className={css.progressSection}>
        <div className={css.progressBar}>
          <div
            className={css.progressFill}
            style={{ width: `${(completedCount / totalCount) * 100}%` }}
          />
        </div>
        <p className={css.progressText}>
          <FormattedMessage
            id="ProviderRequirementsPage.progressText"
            values={{ completed: completedCount, total: totalCount }}
          />
        </p>
      </div>

      <div className={css.requirementsList}>
        {requirements.map(requirement => (
          <div key={requirement.key} className={css.requirementItem}>
            {requirement.completed || requirement.pending ? (
              renderRequirementBody(requirement)
            ) : (
              <RequirementLink
                name={requirement.linkPage}
                hash={requirement.linkHash}
                className={css.requirementLink}
                onClick={onRequirementNavigate}
              >
                {renderRequirementBody(requirement)}
              </RequirementLink>
            )}
          </div>
        ))}
      </div>

      {completedCount === totalCount ? (
        <div className={css.completionMessage}>
          <FormattedMessage id="ProviderRequirementsPage.allRequirementsCompleted" />
        </div>
      ) : null}
    </div>
  );
};

ProviderRequirementsChecklistComponent.propTypes = {
  currentUser: propTypes.currentUser,
  hasListings: bool,
  stripeAccount: propTypes.stripeAccount,
  stripeAccountFetched: bool,
  fetchStripeAccountInProgress: bool,
  fetchStripeAccountError: propTypes.error,
  onFetchCurrentUserHasListings: func,
  onFetchStripeAccount: func,
  onRecheckProviderRequirements: func,
  onRequirementNavigate: func,
};

const mapStateToProps = state => {
  const { currentUser, currentUserHasListings } = state.user;
  const {
    stripeAccount,
    stripeAccountFetched,
    fetchStripeAccountInProgress,
    fetchStripeAccountError,
  } = state.stripeConnectAccount;
  return {
    currentUser,
    hasListings: currentUserHasListings,
    stripeAccount,
    stripeAccountFetched,
    fetchStripeAccountInProgress,
    fetchStripeAccountError,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchCurrentUserHasListings: () => dispatch(fetchCurrentUserHasListings()),
  // Swallow the rethrown error: the reducer already stores it, and an
  // unhandled rejection here would surface as console/Sentry noise.
  onFetchStripeAccount: () => dispatch(fetchStripeAccount()).catch(() => null),
  onRecheckProviderRequirements: () =>
    dispatch(
      recheckProviderRequirements({ source: 'requirements-checklist', refreshUser: true })
    ),
});

const ProviderRequirementsChecklist = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ProviderRequirementsChecklistComponent);

export default ProviderRequirementsChecklist;
